import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class BbdTabbladen extends Component {
  #tabs = [];
  #panels = new Map();
  #observedContent = null;
  #resizeObserver = new ResizeObserver(() => this.#syncPanelHeight());
  #desktopMql = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#tabs.forEach((tab) => {
      this.#panels.set(tab, this.querySelector(`#${tab.getAttribute('aria-controls')}`));
      tab.addEventListener('click', () => this.#activate(tab));
    });
    this.addEventListener('keydown', this.#handleKeyDown);
    this.#syncPanelHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#resizeObserver.disconnect();
  }

  #activate(tab) {
    this.#tabs.forEach((otherTab) => {
      const isActive = otherTab === tab;
      otherTab.setAttribute('aria-selected', String(isActive));
      otherTab.tabIndex = isActive ? 0 : -1;
      this.#panels.get(otherTab)?.toggleAttribute('hidden', !isActive);
    });
    tab.focus({ preventScroll: true });
    // On desktop the tab list itself never moves, so scroll the whole section into
    // view instead of just the button — on mobile (accordion behavior) the button is
    // the right target, since it's the thing that visually jumps.
    const scrollTarget = this.#desktopMql.matches ? (this.closest('.bbd-tabbladen') ?? tab) : tab;
    scrollTarget.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
    this.#syncPanelHeight();
  }

  #syncPanelHeight() {
    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const content = activeTab ? this.#panels.get(activeTab)?.firstElementChild : undefined;

    if (content !== this.#observedContent) {
      this.#resizeObserver.disconnect();
      this.#observedContent = content ?? null;
      if (content) this.#resizeObserver.observe(content);
    }

    const height = content?.getBoundingClientRect().height ?? 0;
    this.style.setProperty('--bbd-tabbladen-content-height', `${height}px`);
  }

  #handleKeyDown = (event) => {
    const currentIndex = this.#tabs.indexOf(event.target);
    if (currentIndex === -1) return;

    const keyToIndex = {
      ArrowDown: currentIndex + 1,
      ArrowUp: currentIndex - 1,
      Home: 0,
      End: this.#tabs.length - 1
    };

    const nextIndex = keyToIndex[event.key];
    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = this.#tabs[(nextIndex + this.#tabs.length) % this.#tabs.length];
    this.#activate(nextTab);
  };
}

if (!customElements.get('bbd-tabbladen-component')) {
  customElements.define('bbd-tabbladen-component', BbdTabbladen);
}
